import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { AvatarFrame } from '../../shared/components/AvatarFrame.js';
import type { MeetingPlayer } from '../types.js';
import { VoterCluster } from '../../shared/components/VoterCluster.js';
import '../../shared/components/CastGrid.css';
import './CastGrid.css';

interface CastGridProps {
  players: MeetingPlayer[];
  ghostIds: readonly string[];
  /** The local viewer's player id. Used to suppress the fellow-Ghost mark on
   *  the local Ghost's own tile — "fellow" requires an other; you are not
   *  your own fellow. */
  meId: string;
  /** voterIds per candidate-id. Empty (or missing) → ribbon collapses. */
  votersByTarget: ReadonlyMap<string, string[]>;
  /** The target the local Ghost has voted for, if any. Drives the voted-state border. */
  myVoteTarget?: string;
  onVote?: (targetId: string) => void;
  /** Cold-arrival suppression; threaded to AvatarFrame + VoterCluster. */
  justArrived?: boolean;
}

/**
 * Ghost POV cast grid. A radiogroup of buttons; tapping a cell casts /
 * re-casts a vote for that player. Each cell carries:
 *  - The candidate's AvatarFrame (with the fellow-Ghost corner dot when the
 *    candidate is in `ghostIds`).
 *  - A VoterCluster overlay positioned at the top edge of the frame
 *    (`top: -13px` relative to the cell). Returns null when nobody has voted,
 *    so the frame reads pristine; renders up to 4 circular voter glyphs with
 *    a "+N" overflow badge beyond that. Ghost-Meeting tops out at 3 voters
 *    so overflow never fires here, but the shape is shared with Discussion
 *    where every alive player can vote and overflow actually engages.
 *
 * Column count is fixed at 3 across all alive-player counts and both
 * viewports — matches MysteryFlipGrid and the clue-giving cast grid, so
 * the cast holds the same layout shape across phase boundaries and
 * across POVs (Ghost meeting ↔ V/F meeting ↔ any clue-giving). 8-player
 * rounds render as 3+3+2 with a trailing orphan row; the earlier dynamic
 * 4-col-for-7-8 rule produced a layout that broke continuity with every
 * other cast surface in the game.
 */
export function CastGrid({
  players,
  ghostIds,
  meId,
  votersByTarget,
  myVoteTarget,
  onVote,
  justArrived = false,
}: CastGridProps): JSX.Element {
  const { t } = useTranslation();
  const ghostIdSet = new Set(ghostIds);
  const playerById = new Map(players.map((p) => [p.id, p]));

  return (
    <div
      className="susdle-cast-grid"
      role="radiogroup"
      aria-label={t('meeting.cast.ariaLabel')}
    >
      {players.map((player) => {
        const voted = myVoteTarget === player.id;
        const voters = votersByTarget.get(player.id) ?? [];
        /* "Fellow" describes the relationship to the local Ghost, so the
           local Ghost's OWN tile never carries the mark. The narrower
           condition (ghost AND not me) also matches the aria-label so SR
           users hear "Vote for Maya" on self-vote rather than the wrong
           "Vote for Maya, fellow Ghost". */
        const isFellow = ghostIdSet.has(player.id) && player.id !== meId;
        const voteLabel = isFellow
          ? t('meeting.cast.voteForFellow', { name: player.name })
          : t('meeting.cast.voteFor', { name: player.name });
        return (
          <div key={player.id}>
            <button
              type="button"
              className="susdle-cast-cell"
              role="radio"
              aria-checked={voted}
              aria-label={voteLabel}
              data-voted={voted ? 'yes' : 'no'}
              data-float-anchor=""
              data-testid={`ballot-vote-${player.id}`}
              onClick={() => onVote?.(player.id)}
            >
              <AvatarFrame
                player={player}
                isHost={false}
                isFellowGhost={isFellow}
                away={!player.connected}
                suppressEntry={justArrived}
              />
              <VoterCluster
                voters={voters
                  .map((id) => playerById.get(id))
                  .filter((p): p is MeetingPlayer => p !== undefined)}
                justArrived={justArrived}
              />
            </button>
          </div>
        );
      })}
    </div>
  );
}
